// WHAT IS BEING SIGNED OFF, PICKED (owner 2026-11-05: "we need all levels and all areas
// and their systems in the scope to signoff ... makes easy complex full selections, to
// multiple systems, areas, levels").
//
// A TREE, not three lists: Project -> Level -> Area -> System, checkable at any depth, so
// "every system in every area on Level 2" is one click and "these three systems in those
// two areas on two levels" is five. It is built from the structure the DRAWING worked out
// (isoModel.structureFrom), so everything on screen is selectable - including a level the
// model has placed nothing on and an area no element sits in yet.
//
// The checked boxes become sign-off targets the way the sign-off reads them
// (isoModel.scopeTargets): a level with no area picked is the whole level, an area with no
// system is the area, and an area with systems is one system-in-area target each.
//
// THE ACTIVITY DROPDOWN (owner 2026-11-05: "an activity dropdown, that automatically
// highlights systems inside areas, and areas inside levels and levels inside the project")
// works the other way round: pick activities and the levels, areas and systems they name
// are checked for you. BA03's activities are called "L1 · Area A · Conduits" and carry the
// level, the area and the system as fields, so this is their own hierarchy, not a guess.
import React, { useMemo } from "react";
import { Alert, Button, Checkbox, Empty, Tag, Tooltip, Tree } from "antd";
import { DRAWN_LAYERS, isLayer, layerIdOf, layerValue, scopeTargets, systemOptions } from "./isoModel";
import ActivityScopeSelect from "./ActivityScopeSelect";

const LEVEL = "L:";
const AREA = "A:";
const SYS = "S:";

export default function ScopePicker({
  structure, levelNames, areas, tiles, manifest, scope, setScope, preview, loading, err,
  canRequest, onRequest, projectId,
}) {
  const levels = useMemo(() => (structure && structure.levels) || [], [structure]);
  const rows = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  const { layers, model } = useMemo(
    () => systemOptions({ areaIds: scope.areaIds, areas, tiles: rows, manifest }), [scope.areaIds, areas, rows, manifest],
  );

  // THE TREE. A system hangs under the area it would be signed off in, because that is what
  // a system target is; the same system under two areas is two different targets.
  const treeData = useMemo(() => {
    const sysNodes = (areaId) => {
      const drawn = layers.filter((l) => l.drawn);
      const kids = [];
      if (drawn.length) {
        kids.push({
          key: `${AREA}${areaId}|drawn`, title: "Drawn layers", selectable: false, checkable: false,
          children: drawn.map((l) => ({ key: `${SYS}${areaId}|${l.value}`, title: l.label, isLeaf: true })),
        });
      }
      const mine = model.filter((m) => m.elements > 0 || ((areas[areaId] || {}).systems || []).includes(m.value));
      if (mine.length) {
        kids.push({
          key: `${AREA}${areaId}|model`, title: "In the model", selectable: false, checkable: false,
          children: mine.map((m) => ({ key: `${SYS}${areaId}|${m.value}`, title: `${m.label} · ${m.note}`, isLeaf: true })),
        });
      }
      return kids;
    };
    const levelNodes = levels.map((l) => {
      const mine = (structure.areasByLevel.get(l.key) || []);
      return {
        key: `${LEVEL}${l.key}`,
        title: `${l.name} · ${mine.length} ${mine.length === 1 ? "area" : "areas"}${l.elements ? ` · ${l.elements.toLocaleString()} el` : " · nothing placed"}`,
        children: mine.map((a) => ({ key: `${AREA}${a.id}`, title: a.name, children: sysNodes(a.id) })),
      };
    });
    const loose = (structure && structure.unlevelled) || [];
    if (loose.length) {
      levelNodes.push({
        key: `${LEVEL}`, title: `On no single level · ${loose.length} areas`, checkable: false,
        children: loose.map((a) => ({ key: `${AREA}${a.id}`, title: a.name, children: sysNodes(a.id) })),
      });
    }
    return levelNodes;
  }, [levels, structure, layers, model, areas]);

  // the scope as checked keys, and back
  const checked = useMemo(() => {
    const out = [];
    (scope.levelKeys || []).forEach((k) => out.push(`${LEVEL}${k}`));
    (scope.areaIds || []).forEach((id) => {
      if (!(scope.systems || []).length) out.push(`${AREA}${id}`);
      (scope.systems || []).forEach((s) => out.push(`${SYS}${id}|${s}`));
    });
    return out;
  }, [scope]);

  const onCheck = (keys) => {
    const all = Array.isArray(keys) ? keys : keys.checked || [];
    const levelKeys = []; const areaIds = new Set(); const systems = new Set();
    for (const k of all) {
      const key = String(k);
      if (key.startsWith(LEVEL)) { const v = key.slice(LEVEL.length); if (v) levelKeys.push(v); continue; }
      if (key.startsWith(AREA)) { const v = key.slice(AREA.length); if (v && !v.includes("|")) areaIds.add(v); continue; }
      if (key.startsWith(SYS)) {
        const [areaId, sys] = key.slice(SYS.length).split("|");
        if (areaId) areaIds.add(areaId);
        if (sys) systems.add(sys);
      }
    }
    setScope({ ...scope, levelKeys, areaIds: [...areaIds], systems: [...systems] });
  };

  const targets = useMemo(() => scopeTargets(scope, { areas, levels: levelNames || {} }), [scope, areas, levelNames]);
  const labelOf = (t) => {
    if (t.kind === "level") return t.level;
    const a = areas[t.areaSetId] || {};
    return [t.system, a.name, a.level].filter(Boolean).join(" · ");
  };
  const notDrawn = (scope.systems || []).filter(isLayer)
    .filter((s) => !layers.find((l) => l.value === s && l.drawn))
    .map((s) => (DRAWN_LAYERS.find((l) => l.id === layerIdOf(s)) || {}).label || layerIdOf(s));

  return (
    <div className="signoff-scope" data-testid="signoff-scope">
      <div className="signoff-panel-title">The scope to sign off</div>
      <ActivityScopeSelect projectId={projectId} structure={structure} areas={areas}
                           onPick={({ levelKeys, areaIds, systems }) => setScope({
                             ...scope,
                             levelKeys: [...new Set([...(scope.levelKeys || []), ...levelKeys])],
                             areaIds: [...new Set([...(scope.areaIds || []), ...areaIds])],
                             systems: [...new Set([...(scope.systems || []), ...systems])],
                           })}
                           onReplace={({ levelKeys, areaIds, systems }) => setScope({ ...scope, levelKeys, areaIds, systems })} />
      {treeData.length ? (
        <Tree checkable selectable={false} checkedKeys={checked} onCheck={onCheck} treeData={treeData}
              height={320} defaultExpandedKeys={levels.slice(0, 1).map((l) => `${LEVEL}${l.key}`)}
              className="signoff-scope-tree" data-testid="scope-tree" />
      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No level is known yet" />}
      <Checkbox className="mt-2" checked={!!scope.only} onChange={(e) => setScope({ ...scope, only: e.target.checked })} data-testid="scope-only">
        <Tooltip title="Draw only what is in the scope; the rest of the building stays pale.">Show the scope alone</Tooltip>
      </Checkbox>

      <div className="signoff-scope-targets" data-testid="scope-targets">
        {targets.length ? targets.slice(0, 40).map((t, i) => (
          <Tag key={i} color={t.kind === "system" ? "geekblue" : t.kind === "area" ? "blue" : "cyan"}>{labelOf(t)}</Tag>
        )) : <span className="signoff-muted">Tick a level, an area, or a system inside an area</span>}
        {targets.length > 40 ? <Tag>+{targets.length - 40} more</Tag> : null}
      </div>
      {notDrawn.length ? (
        <Alert type="info" showIcon className="mb-2" data-testid="scope-not-drawn"
               message={`${notDrawn.join(", ")} ${notDrawn.length === 1 ? "is" : "are"} not in this project's drawings yet`}
               description="The sign-off is still recorded against it; nothing is drawn for it and it covers no model elements." />
      ) : null}
      {err ? <Alert type="error" showIcon className="mb-2" message={err} /> : null}
      {targets.length ? (
        <div className="signoff-scope-facts">
          {!canRequest ? <small className="signoff-muted">The drawing shows what is signed off; asking for a sign-off is for the project owner, an admin or a schedule editor.</small>
            : loading ? <small className="signoff-muted">Working out what this covers…</small> : preview ? (
              <>
                <div className="tile-row"><span>Targets</span><b>{targets.length.toLocaleString()}</b></div>
                <div className="tile-row"><span>Elements</span><b>{preview.elements.toLocaleString()}</b></div>
                <div className="tile-row"><span>Activities starred</span><b>{preview.activityCount.toLocaleString()}</b></div>
                {preview.activities.length ? (
                  <small className="signoff-muted d-block" data-testid="scope-activities">
                    {preview.activities.slice(0, 8).map((a) => a.name).join(" · ")}
                    {preview.activities.length > 8 ? ` +${preview.activities.length - 8} more` : ""}
                  </small>
                ) : <small className="signoff-muted d-block">No schedule activity is tied to this scope yet - the sign-off is still recorded.</small>}
                {preview.coverNote ? <Alert type="warning" showIcon className="mt-2" message={preview.coverNote} /> : null}
              </>
            ) : null}
        </div>
      ) : null}
      <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
        <Tooltip title="Clear the levels, areas and systems picked">
          <Button onClick={() => setScope({ levelKeys: [], areaIds: [], systems: [], only: false })} data-testid="scope-clear">Clear</Button>
        </Tooltip>
        {canRequest ? (
          <Tooltip title="Ask the owner/GC, the contractor or a trade partner to sign this scope off by email">
            <Button type="primary" disabled={!targets.length} onClick={() => onRequest(targets)} data-testid="scope-request">Request sign-off</Button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
export { LEVEL, AREA, SYS, layerValue };
